import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getAllUsers, updateUserRole, createUserRole, deactivateUser } from '../../utils/roleMapping';
import { ROLES, getAllRoleNames } from '../../constants/roles';
import { hasPermission } from '../../utils/permissions';
import { PERMISSIONS } from '../../constants/roles';

const UserManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', role: 'talent-partner' });
  const [editingUser, setEditingUser] = useState(null);

  // Always call useEffect before any conditional returns
  useEffect(() => {
    // Only load users if user has permission
    if (hasPermission(user, PERMISSIONS.MANAGE_USERS)) {
      loadUsers();
    }
  }, [user]);

  // Check if user has permission to manage users
  if (!hasPermission(user, PERMISSIONS.MANAGE_USERS)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to manage users.</p>
        </div>
      </div>
    );
  }

  const loadUsers = async () => {
    try {
      setLoading(true);
      const userData = await getAllUsers();
      setUsers(userData);
      setError(null);
    } catch (err) {
      setError('Failed to load users');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const success = await createUserRole(
        newUser.email,
        newUser.role
      );
      
      if (success) {
        setNewUser({ email: '', role: 'talent-partner' });
        setShowAddUser(false);
        await loadUsers();
      } else {
        setError('Failed to create user');
      }
    } catch (err) {
      setError('Failed to create user');
      console.error('Error creating user:', err);
    }
  };

  const handleUpdateRole = async (email, newRole, notes = '') => {
    try {
      const success = await updateUserRole(
        email,
        newRole
      );
      
      if (success) {
        await loadUsers();
        setEditingUser(null);
      } else {
        setError('Failed to update user role');
      }
    } catch (err) {
      setError('Failed to update user role');
      console.error('Error updating user role:', err);
    }
  };

  const handleDeactivateUser = async (email, reason = '') => {
    if (window.confirm(`Are you sure you want to deactivate ${email}?`)) {
      try {
        const success = await deactivateUser(email);
        
        if (success) {
          await loadUsers();
        } else {
          setError('Failed to deactivate user');
        }
      } catch (err) {
        setError('Failed to deactivate user');
        console.error('Error deactivating user:', err);
      }
    }
  };

  const getRoleDisplayName = (roleName) => {
    const role = Object.values(ROLES).find(r => r.name === roleName);
    return role ? role.displayName : roleName;
  };

  const getRoleBadgeColor = (roleName) => {
    switch (roleName) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'talent-partner':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading users...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">User Management</h1>
        <p className="text-gray-600">Manage user roles and permissions for the Satellite Talent Dashboard</p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Add User Section */}
      <div className="mb-6">
        <button
          onClick={() => setShowAddUser(!showAddUser)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
        >
          {showAddUser ? 'Cancel' : 'Add New User'}
        </button>

        {showAddUser && (
          <div className="mt-4 bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Add New User</h3>
            <form onSubmit={handleAddUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="user@satellitetalent.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {getAllRoleNames().map(roleName => (
                    <option key={roleName} value={roleName}>
                      {getRoleDisplayName(roleName)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium"
                >
                  Add User
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddUser(false)}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            Current Users ({users.length})
          </h2>
        </div>

        {users.length === 0 ? (
          <div className="px-6 py-8 text-center text-gray-500">
            No users found. Add the first user to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((userData, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {userData.Email || userData.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingUser === userData.Email ? (
                        <select
                          value={userData.Role || userData.role}
                          onChange={(e) => handleUpdateRole(userData.Email || userData.email, e.target.value)}
                          className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                        >
                          {getAllRoleNames().map(roleName => (
                            <option key={roleName} value={roleName}>
                              {getRoleDisplayName(roleName)}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(userData.Role || userData.role)}`}>
                          {getRoleDisplayName(userData.Role || userData.role)}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        (userData.Status || userData.status) === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {userData.Status || userData.status || 'active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      {editingUser === userData.Email ? (
                        <button
                          onClick={() => setEditingUser(null)}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          Cancel
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => setEditingUser(userData.Email || userData.email)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Edit
                          </button>
                          {(userData.Status || userData.status) === 'active' && (
                            <button
                              onClick={() => handleDeactivateUser(userData.Email || userData.email)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Deactivate
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Role Information */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">Role Permissions</h3>
        <div className="grid md:grid-cols-2 gap-6">
          {Object.values(ROLES).map(role => (
            <div key={role.name} className="bg-white p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">{role.displayName}</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                {role.permissions.map(permission => (
                  <li key={permission} className="flex items-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                    {permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
